import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown, ArrowLeft, ArrowUp, ChevronRight, ChevronsUpDown, FileDown, FileText, Lock, Send, Trash2,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  actualizarFechaLimite,
  actualizarMesFlujo,
  borrarFactura,
  calcularTbfin,
  descargarFacturaPdfById,
  getFactura,
  getFacturasTodas,
  type FacturacionDia,
  type FacturacionResultado,
  type PreliminarFactura,
} from "@/lib/api";
import { usePermisos } from "@/lib/authz";
import { ResumenCel } from "@/components/ResumenCel";
import { ResumenPotencia } from "@/components/ResumenPotencia";

const mxn = (n: number) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n);
const money = (n: number, moneda = "MXN") =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: moneda === "USD" ? "USD" : "MXN" }).format(n);
const num = (n: number, d = 2) =>
  new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);

// ---------- Lista de facturas guardadas (/facturas) --------------------------

export function FacturasPage() {
  // Para marcar en ámbar las facturas cuya fecha límite de pago ya pasó.
  const hoyIso = new Date().toISOString().slice(0, 10);
  const q = useQuery({ queryKey: ["facturas-todas"], queryFn: getFacturasTodas });
  const facturas = q.data ?? [];

  const { has } = usePermisos();
  const puedeBorrar = has("borrar_factura");
  const qc = useQueryClient();
  const [delError, setDelError] = useState<string | null>(null);
  const borrar = useMutation({
    mutationFn: (id: number) => borrarFactura(id),
    onSuccess: () => {
      setDelError(null);
      qc.invalidateQueries({ queryKey: ["facturas-todas"] });
    },
    onError: (e) => setDelError(e instanceof Error ? e.message : "No se pudo borrar la factura."),
  });

  // Filtros: por contrato y por periodo (Año-Mes). El mes filtra por TRASLAPE:
  // entra toda factura cuyo periodo TOCA ese mes, aunque empiece o termine fuera
  // (las quincenas que cruzan de mes aparecen en ambos meses).
  // Viven en la URL (?contrato=&mes=) para que sobrevivan al abrir una factura
  // y regresar (el detalle regresa a /facturas con el mismo query string).
  const [searchParams, setSearchParams] = useSearchParams();
  const fContrato = searchParams.get("contrato") ?? "";
  const fMes = searchParams.get("mes") ?? ""; // "2026-06" o "" = todos
  const ponFiltros = (contrato: string, mes: string) => {
    const p = new URLSearchParams();
    if (contrato) p.set("contrato", contrato);
    if (mes) p.set("mes", mes);
    setSearchParams(p, { replace: true });
  };
  const setFContrato = (v: string) => ponFiltros(v, fMes);
  const setFMes = (v: string) => ponFiltros(fContrato, v);
  const contratosDisp = useMemo(
    () => Array.from(new Set(facturas.map((f) => f.folio_contrato))).sort(),
    [facturas],
  );
  const facturasFiltradas = useMemo(() => {
    // último día del mes: primer día del mes siguiente − 1 (evita hardcodear 28/30/31)
    const finMes = (ym: string) => {
      const [a, m] = ym.split("-").map(Number);
      const d = new Date(a, m, 0); // día 0 del mes siguiente = último del mes
      return `${ym}-${String(d.getDate()).padStart(2, "0")}`;
    };
    return facturas.filter(
      (f) =>
        (!fContrato || f.folio_contrato === fContrato) &&
        (!fMes || (f.desde <= finMes(fMes) && f.hasta >= `${fMes}-01`)),
    );
  }, [facturas, fContrato, fMes]);

  // Orden de la tabla (click en encabezados). Por defecto, periodo más reciente primero.
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "desde",
    dir: "desc",
  });
  const facturasOrdenadas = useMemo(() => {
    const arr = [...facturasFiltradas];
    arr.sort((a, b) => {
      let cmp: number;
      switch (sort.key) {
        case "id": cmp = a.id - b.id; break;
        case "subtotal": cmp = a.subtotal - b.subtotal; break;
        case "iva": cmp = a.iva - b.iva; break;
        case "total": cmp = a.total - b.total; break;
        case "folio_contrato": cmp = a.folio_contrato.localeCompare(b.folio_contrato, "es"); break;
        case "contraparte": cmp = a.contraparte.localeCompare(b.contraparte, "es"); break;
        case "desde": cmp = a.desde.localeCompare(b.desde); break;
        case "fecha_limite_pago": {
          // Las facturas sin fecha límite van SIEMPRE al final (en asc y en
          // desc): no tener fecha no es vencer antes ni después.
          const x = a.fecha_limite_pago, y = b.fecha_limite_pago;
          if (!x && !y) cmp = 0;
          else if (!x) return 1;
          else if (!y) return -1;
          else cmp = x.localeCompare(y);
          break;
        }
        case "guardado_en": cmp = a.guardado_en.localeCompare(b.guardado_en); break;
      }
      if (cmp === 0) cmp = a.id - b.id; // desempate estable
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [facturasFiltradas, sort]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Facturas guardadas</h1>
        <p className="text-sm text-slate-500 mt-1">
          Liquidaciones guardadas con su memoria de cálculo. Abre una para descargar el PDF o enviarla.
        </p>
      </div>

      {delError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{delError}</div>
      )}

      {/* Filtros: contrato + mes (por traslape de periodo) */}
      <div className="bg-white border rounded-lg px-4 py-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Contrato</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm w-56"
            value={fContrato}
            onChange={(e) => setFContrato(e.target.value)}
          >
            <option value="">Todos</option>
            {contratosDisp.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Periodo (Año-Mes)</span>
          <input
            type="month"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={fMes}
            onChange={(e) => setFMes(e.target.value)}
            title="Muestra las facturas cuyo periodo toca este mes"
          />
        </label>
        {(fContrato || fMes) && (
          <button
            type="button"
            onClick={() => ponFiltros("", "")}
            className="text-sm text-slate-500 hover:text-slate-800 underline pb-2"
          >
            Limpiar filtros
          </button>
        )}
        <span className="text-[11px] text-slate-400 pb-2 ml-auto">
          {facturasFiltradas.length} de {facturas.length} factura{facturas.length === 1 ? "" : "s"}
          {fMes && " · el mes filtra por traslape del periodo"}
        </span>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        {q.isLoading ? (
          <div className="p-4 text-sm text-slate-500">Cargando…</div>
        ) : facturas.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            Aún no hay facturas guardadas. Genera una en <b>Cálculo de facturación</b> y presiona
            “Guardar factura”.
          </div>
        ) : facturasFiltradas.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            Ninguna factura {fContrato && <>de <b>{fContrato}</b> </>}
            {fMes && <>toca el mes <b>{fMes}</b></>}. Ajusta o limpia los filtros.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <Th label="#" k="id" sort={sort} setSort={setSort} />
                <Th label="Contrato" k="folio_contrato" sort={sort} setSort={setSort} />
                <Th label="Contraparte" k="contraparte" sort={sort} setSort={setSort} />
                <Th label="Periodo" k="desde" sort={sort} setSort={setSort} />
                <Th label="Subtotal (sin IVA)" k="subtotal" sort={sort} setSort={setSort} align="right" />
                <Th label="IVA" k="iva" sort={sort} setSort={setSort} align="right" />
                <Th label="Total" k="total" sort={sort} setSort={setSort} align="right" />
                <Th label="Vence" k="fecha_limite_pago" sort={sort} setSort={setSort} />
                <Th label="Guardada" k="guardado_en" sort={sort} setSort={setSort} />
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {facturasOrdenadas.map((f) => (
                <tr key={f.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-500">{f.id}</td>
                  <td className="px-4 py-2 font-medium">
                    {f.folio_contrato}
                    {f.producto === "Potencia" && (
                      // La parcialidad distingue dos facturas del mismo mes
                      // (001MPP/RIC cobra los pagos 1 y 2 en mayo-2026).
                      <span className="ml-1.5 text-[10px] font-medium bg-indigo-100 text-indigo-700 rounded-full px-1.5 py-0.5 align-middle">
                        Potencia{f.n_pago ? ` · Pago ${f.n_pago}` : ""}
                      </span>
                    )}
                    {(f.producto === "CEL" || f.producto === "i-REC") && (
                      <span className="ml-1.5 text-[10px] font-medium bg-teal-100 text-teal-700 rounded-full px-1.5 py-0.5 align-middle">
                        {f.producto}{f.n_pago ? ` · Entrega ${f.n_pago}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">{f.contraparte}</td>
                  <td className="px-4 py-2">
                    {f.desde} a {f.hasta}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(f.subtotal, f.moneda)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-500">{money(f.iva, f.moneda)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-medium">{money(f.total, f.moneda)}</td>
                  {/* Fecha límite de pago (mig 083): el plazo de la OC contado
                      desde el fin del periodo, editable en el detalle. */}
                  <td className="px-4 py-2 text-xs">
                    {f.fecha_limite_pago ? (
                      <span
                        className={
                          f.fecha_limite_pago < hoyIso
                            ? "font-medium text-amber-700"
                            : "text-slate-600"
                        }
                        title={
                          f.fecha_limite_pago < hoyIso
                            ? "La fecha límite de pago ya pasó"
                            : "Fecha límite de pago"
                        }
                      >
                        {f.fecha_limite_pago}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {new Date(f.guardado_en).toLocaleString("es-MX")}
                    {f.guardado_por ? ` · ${f.guardado_por}` : ""}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center gap-3">
                      <Link
                        to={`/facturas/${f.id}`}
                        // el detalle usa `volver` para regresar con los filtros puestos
                        state={{ volver: searchParams.toString() }}
                        className="text-blue-600 hover:underline"
                      >
                        Abrir →
                      </Link>
                      {puedeBorrar && f.enviada_flamma && (
                        <span title="Enviada a Flamma — no se puede borrar" className="text-slate-300">
                          <Lock className="w-4 h-4" />
                        </span>
                      )}
                      {puedeBorrar && !f.enviada_flamma && (
                        <button
                          type="button"
                          title="Borrar factura"
                          disabled={borrar.isPending}
                          onClick={() => {
                            if (
                              confirm(
                                `¿Borrar la factura #${f.id} (${f.folio_contrato}, ${f.desde} a ${f.hasta})?\n\nEsta acción no se puede deshacer.`,
                              )
                            ) {
                              borrar.mutate(f.id);
                            }
                          }}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

type SortKey =
  | "id" | "folio_contrato" | "contraparte" | "desde"
  | "subtotal" | "iva" | "total" | "fecha_limite_pago" | "guardado_en";

// Encabezado de columna clicable: ordena por su clave; alterna asc/desc si ya está activo.
function Th({
  label, k, sort, setSort, align = "left",
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; dir: "asc" | "desc" };
  setSort: (s: { key: SortKey; dir: "asc" | "desc" }) => void;
  align?: "left" | "right";
}) {
  const active = sort.key === k;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  return (
    <th className={`px-4 py-2 ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={() =>
          setSort(active ? { key: k, dir: sort.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "asc" })
        }
        className={`inline-flex items-center gap-1 hover:text-slate-700 ${
          align === "right" ? "flex-row-reverse" : ""
        } ${active ? "text-slate-700 font-semibold" : ""}`}
        title="Ordenar por esta columna"
      >
        <span>{label}</span>
        <Icon className={`w-3 h-3 ${active ? "text-slate-500" : "text-slate-300"}`} />
      </button>
    </th>
  );
}

// ---------- Detalle de una factura (/facturas/:id) ---------------------------

export function FacturaDetallePage() {
  const { id } = useParams<{ id: string }>();
  const facturaId = Number(id);
  const q = useQuery({
    queryKey: ["factura", facturaId],
    queryFn: () => getFactura(facturaId),
    enabled: Number.isFinite(facturaId),
  });

  const [error, setError] = useState<string | null>(null);
  const [flamma, setFlamma] = useState<string | null>(null);

  const { has } = usePermisos();
  const navigate = useNavigate();
  // Filtros con los que se abrió desde la lista (query string en el state del
  // Link "Abrir"): al regresar se restauran en la URL de /facturas.
  const volver = (useLocation().state as { volver?: string } | null)?.volver;
  const volverUrl = volver ? `/facturas?${volver}` : "/facturas";
  const qc = useQueryClient();
  const borrar = useMutation({
    mutationFn: () => borrarFactura(facturaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["facturas-todas"] });
      navigate(volverUrl);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo borrar la factura."),
  });

  const descargarPdf = async () => {
    setError(null);
    try {
      await descargarFacturaPdfById(facturaId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al generar el PDF");
    }
  };

  const det = q.data;
  const res = det?.resultado;

  // Mes de flujo B2B (solo facturas preliminar/ajuste): editable aquí.
  const [mesFlujo, setMesFlujo] = useState("");
  const [mesFlujoPrel, setMesFlujoPrel] = useState("");
  useEffect(() => {
    setMesFlujo(det?.mes_flujo ?? "");
    setMesFlujoPrel(det?.mes_flujo_preliminar ?? "");
  }, [det?.mes_flujo, det?.mes_flujo_preliminar]);
  // Fecha límite de pago (mig 083): editable aquí cuando la emisión real no
  // coincidió con el precálculo, o cuando la OC pactó una fecha fija.
  const [fechaLimite, setFechaLimite] = useState("");
  // prepago_gas: la preliminar y el ajuste son dos documentos con su propio
  // vencimiento (mig 084); el de arriba es el del documento vigente.
  const [fechaLimitePrel, setFechaLimitePrel] = useState("");
  useEffect(() => {
    setFechaLimite(det?.fecha_limite_pago ?? "");
    setFechaLimitePrel(det?.fecha_limite_pago_preliminar ?? "");
  }, [det?.fecha_limite_pago, det?.fecha_limite_pago_preliminar]);
  const esAjustado = det?.tipo === "ajuste";
  const guardarFechaLimite = useMutation({
    mutationFn: () => actualizarFechaLimite(
      facturaId, fechaLimite || null,
      esAjustado ? fechaLimitePrel || null : undefined),
    onSuccess: (d) => {
      qc.setQueryData(["factura", facturaId], d);
      qc.invalidateQueries({ queryKey: ["facturas-todas"] });
      qc.invalidateQueries({ queryKey: ["facturas"] });
      setError(null);
    },
    onError: (e) =>
      setError(e instanceof Error ? e.message : "No se pudo cambiar la fecha límite de pago."),
  });

  const guardarMesFlujo = useMutation({
    mutationFn: () => actualizarMesFlujo(facturaId, mesFlujo, mesFlujoPrel || null),
    onSuccess: (d) => {
      qc.setQueryData(["factura", facturaId], d);
      qc.invalidateQueries({ queryKey: ["facturas-todas"] });
      setError(null);
    },
    onError: (e) =>
      setError(e instanceof Error ? e.message : "No se pudo cambiar el mes de flujo."),
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to={volverUrl} className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="w-4 h-4" /> Facturas
        </Link>
      </div>

      {q.isLoading && <div className="text-sm text-slate-500">Cargando factura…</div>}
      {q.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          No se pudo cargar la factura.
        </div>
      )}

      {det && res && (det.tipo === "preliminar" || det.tipo === "ajuste") && (
        <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-3">
          <div className="text-sm text-slate-700">
            <span className="text-[11px] font-medium bg-sky-100 text-sky-700 rounded-full px-2 py-0.5 mr-2 uppercase">
              {det.tipo}
            </span>
            Flujo en /b2b/utilidad
          </div>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">
              {det.tipo === "ajuste" ? "Mes del ajuste (diferencia)" : "Afecta el mes"}
            </span>
            <input
              type="month"
              className="mt-1 border rounded px-2 py-1.5 text-sm"
              value={mesFlujo}
              onChange={(e) => e.target.value && setMesFlujo(e.target.value)}
            />
          </label>
          {det.tipo === "ajuste" && det.mes_flujo_preliminar && (
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Mes de la preliminar</span>
              <input
                type="month"
                className="mt-1 border rounded px-2 py-1.5 text-sm"
                value={mesFlujoPrel}
                onChange={(e) => e.target.value && setMesFlujoPrel(e.target.value)}
              />
            </label>
          )}
          <button
            type="button"
            onClick={() => guardarMesFlujo.mutate()}
            disabled={guardarMesFlujo.isPending || !mesFlujo
              || (mesFlujo === (det.mes_flujo ?? "")
                  && mesFlujoPrel === (det.mes_flujo_preliminar ?? ""))}
            className="inline-flex items-center gap-2 bg-slate-800 text-white rounded px-4 py-2 text-sm hover:bg-slate-900 disabled:opacity-50"
          >
            {guardarMesFlujo.isPending ? "Guardando…" : "Cambiar mes"}
          </button>
          <span className="text-[11px] text-slate-400 pb-2">
            {det.mes_flujo
              ? `Hoy cae en ${det.mes_flujo}`
              : "Sin mes asignado: cae en el mes de operación"}
          </span>
        </div>
      )}

      {det && res && (
        <div className="bg-white border rounded-lg p-4 flex flex-wrap items-end gap-3">
          {/* prepago_gas ajustado: una fecha por documento (preliminar y ajuste) */}
          {esAjustado && (
            <label className="block">
              <span className="text-xs font-medium text-slate-600">
                Fecha límite — preliminar
              </span>
              <input
                type="date"
                className="mt-1 border rounded px-2 py-1.5 text-sm"
                value={fechaLimitePrel}
                onChange={(e) => setFechaLimitePrel(e.target.value)}
              />
            </label>
          )}
          <label className="block">
            <span className="text-xs font-medium text-slate-600">
              {esAjustado ? "Fecha límite — ajuste" : "Fecha límite de pago"}
            </span>
            <input
              type="date"
              className="mt-1 border rounded px-2 py-1.5 text-sm"
              value={fechaLimite}
              onChange={(e) => setFechaLimite(e.target.value)}
            />
          </label>
          <button
            type="button"
            onClick={() => guardarFechaLimite.mutate()}
            disabled={
              guardarFechaLimite.isPending
              || (fechaLimite === (det.fecha_limite_pago ?? "")
                  && fechaLimitePrel === (det.fecha_limite_pago_preliminar ?? ""))
            }
            className="inline-flex items-center gap-2 bg-slate-800 text-white rounded px-4 py-2 text-sm hover:bg-slate-900 disabled:opacity-50"
          >
            {guardarFechaLimite.isPending ? "Guardando…" : "Cambiar fecha"}
          </button>
          <span className="text-xs text-slate-500 pb-2">
            {esAjustado
              ? "La preliminar y el ajuste se cobran por separado: captura el vencimiento de cada documento."
              : det.fecha_limite_pago
                ? `El comprador debe pagar a más tardar el ${det.fecha_limite_pago}.`
                : "Sin fecha límite capturada (la OC de este contrato pactó fechas fijas, o se guardó antes de tener el plazo)."}
          </span>
        </div>
      )}

      {det && res && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">
                Factura #{det.id} — {res.folio}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {res.contraparte} · {res.desde} a {res.hasta} · guardada{" "}
                {new Date(det.guardado_en).toLocaleString("es-MX")}
                {det.guardado_por ? ` por ${det.guardado_por}` : ""}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={descargarPdf}
                className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700"
              >
                <FileDown className="w-4 h-4" /> Generar PDF
              </button>
              <button
                type="button"
                onClick={() => setFlamma("Enviar a Flamma: funcionalidad en desarrollo (próximamente).")}
                className="inline-flex items-center gap-2 bg-violet-600 text-white rounded px-4 py-2 text-sm hover:bg-violet-700"
              >
                <Send className="w-4 h-4" /> Enviar a Flamma
              </button>
              {has("borrar_factura") && !det.enviada_flamma && (
                <button
                  type="button"
                  disabled={borrar.isPending}
                  onClick={() => {
                    if (
                      confirm(
                        `¿Borrar la factura #${det.id} (${res.folio}, ${res.desde} a ${res.hasta})?\n\nEsta acción no se puede deshacer.`,
                      )
                    ) {
                      setError(null);
                      borrar.mutate();
                    }
                  }}
                  className="inline-flex items-center gap-2 border border-red-300 text-red-700 rounded px-4 py-2 text-sm hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" /> {borrar.isPending ? "Borrando…" : "Borrar"}
                </button>
              )}
              {has("borrar_factura") && det.enviada_flamma && (
                <span className="inline-flex items-center gap-2 text-slate-400 text-sm px-2">
                  <Lock className="w-4 h-4" /> Enviada a Flamma
                </span>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
          )}
          {flamma && (
            <div className="bg-violet-50 border border-violet-200 text-violet-800 rounded p-3 text-sm">
              {flamma}
            </div>
          )}

          {det.resultado_potencia ? (
            // Factura de POTENCIA: vista CFDI de la parcialidad (mig 077)
            <ResumenPotencia r={det.resultado_potencia} />
          ) : det.resultado_cel ? (
            // Factura de CERTIFICADOS (CEL / i-REC): misma vista CFDI
            <ResumenCel r={det.resultado_cel} />
          ) : (
            <>
              {det.preliminar && <PanelPreliminarAjuste pre={det.preliminar} res={res} />}
              {/* con el panel de desglose, la narrativa del ajuste (1ª advertencia
                  del adaptador) sale sobrando — se omite para no duplicar */}
              <ResumenFactura
                res={det.preliminar
                  ? { ...res, advertencias: res.advertencias.slice(1) }
                  : res}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Desglose preliminar + ajuste (esquema prepago_gas) ----------------

/** Tabla con los montos de la PRELIMINAR y del AJUSTE, el mes de flujo que
 *  afecta cada documento (B2B) y el valor FINAL del mes de operación. */
function PanelPreliminarAjuste({ pre, res }: {
  pre: PreliminarFactura; res: FacturacionResultado;
}) {
  const m = pre.moneda;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const esAjuste = pre.modo === "ajuste";
  // La preliminar guardada (solo en ajustes): subtotal tal cual se emitió.
  const subPre = esAjuste ? pre.subtotal_preliminar ?? null : pre.subtotal;
  const celdas = "px-4 py-2 text-right tabular-nums whitespace-nowrap";
  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
        <span className="font-medium text-slate-800">
          Preliminar y ajuste por gas — mes de operación {pre.ym}
        </span>
        <span className="text-xs text-slate-500">
          prepago §20 al gas promedio · ajuste §18 al gas efectivo
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 text-left">
            <tr className="border-b">
              <th className="px-4 py-2 font-medium">Documento</th>
              <th className={`${celdas} font-medium`}>Gas (USD/MMBtu)</th>
              <th className={`${celdas} font-medium`}>Subtotal</th>
              <th className={`${celdas} font-medium`}>IVA ({num(pre.iva_pct * 100, 0)}%)</th>
              <th className={`${celdas} font-medium`}>Total</th>
              <th className={`${celdas} font-medium`}>Vence</th>
              <th className={`${celdas} font-medium`}>Afecta el mes (B2B)</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            <tr className="border-b">
              <td className="px-4 py-2">Factura preliminar (prepago)</td>
              <td className={celdas}>
                {esAjuste
                  ? pre.gas_preliminar != null ? num(pre.gas_preliminar, 4) : "—"
                  : num(pre.gas_usado, 4)}
              </td>
              <td className={celdas}>{subPre != null ? money(subPre, m) : "—"}</td>
              <td className={celdas}>{subPre != null ? money(r2(subPre * pre.iva_pct), m) : "—"}</td>
              <td className={celdas}>{subPre != null ? money(r2(subPre * (1 + pre.iva_pct)), m) : "—"}</td>
              <td className={celdas}>
                {(esAjuste ? pre.fecha_limite_pago_preliminar : pre.fecha_limite_pago) ?? "—"}
              </td>
              <td className={celdas}>
                {(esAjuste ? pre.mes_flujo_preliminar : pre.mes_flujo) ?? "—"}
              </td>
            </tr>
            {esAjuste ? (
              <tr className="border-b">
                <td className="px-4 py-2">
                  Ajuste por gas
                  {pre.tipo_ajuste && (
                    <span className="block text-xs text-slate-500">{pre.tipo_ajuste}</span>
                  )}
                </td>
                <td className={celdas}>{num(pre.gas_usado, 4)}</td>
                <td className={celdas}>{pre.dif_subtotal != null ? money(pre.dif_subtotal, m) : "—"}</td>
                <td className={celdas}>{pre.dif_iva != null ? money(pre.dif_iva, m) : "—"}</td>
                <td className={`${celdas} font-medium ${(pre.dif_total ?? 0) < 0 ? "text-red-700" : "text-emerald-700"}`}>
                  {pre.dif_total != null ? money(pre.dif_total, m) : "—"}
                </td>
                <td className={celdas}>{pre.fecha_limite_pago ?? "—"}</td>
                <td className={celdas}>{pre.mes_flujo ?? "—"}</td>
              </tr>
            ) : (
              <tr className="border-b">
                <td className="px-4 py-2 text-slate-500" colSpan={7}>
                  Ajuste pendiente: al cerrar el mes operado se recalcula el CE con el
                  gas efectivo y la diferencia se liquida (factura complementaria o
                  nota de crédito).
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300">
              <td className="px-4 py-2">
                Valor final del mes {pre.ym}
                <span className="block text-xs font-normal text-slate-500">
                  lo que guarda esta factura{esAjuste ? "" : " (antes del ajuste)"}
                </span>
              </td>
              <td className={celdas}>{esAjuste ? num(pre.gas_usado, 4) : ""}</td>
              <td className={celdas}>{money(res.subtotal, m)}</td>
              <td className={celdas}>{money(res.iva, m)}</td>
              <td className={celdas}>{money(res.total, m)}</td>
              <td className={celdas}></td>
              <td className={`${celdas} font-normal text-xs text-slate-500`}>
                operación {pre.ym}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ---------- Componente compartido: resumen + detalle diario ------------------

export function ResumenFactura({ res }: { res: FacturacionResultado }) {
  // Una sola colección de claves abiertas (conceptos y días).
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (k: string) =>
    setOpen((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  // Componentes del CF_q, reconstruidos del detalle diario (igual que el backend).
  const baseFijo = res.dias_detalle.reduce((s, d) => s + d.ee * 24 * res.pf, 0);
  const upliftFijo = res.dias_detalle.reduce(
    (s, d) => s + d.horas_activas * d.ee * (res.pf_tbfin - res.pf),
    0,
  );
  const ivaTxt = `${(res.iva_pct * 100).toFixed(0)}%`;
  const m = res.moneda;
  const esUsd = m === "USD";

  // ---- Insumos del periodo: los valores con los que se calcularon CE y CTreg.
  // Vienen del backend (insumos_q); las facturas guardadas antes de esto no los
  // traen, así que cada línea se omite si el insumo no existe.
  const ins = new Map((res.insumos_q ?? []).map((i) => [i.nombre, i]));
  const fmtIns = (nombre: string, d = 4): string | null => {
    const i = ins.get(nombre);
    if (!i) return null;
    return i.constante
      ? num(i.minimo, d)
      : `${num(i.minimo, d)} – ${num(i.maximo, d)} (prom. ${num(i.promedio, d)})`;
  };
  /** Agrega [etiqueta, valor] solo si el insumo existe. */
  const lineaIns = (etiqueta: string, nombre: string, d = 4): [string, string][] => {
    const v = fmtIns(nombre, d);
    return v ? [[etiqueta, v]] : [];
  };
  // MWh base de la liquidación = Σ (horas activadas × EE). Denominador de los
  // promedios ponderados de CEU_d / PE y TReg.
  const mwhBase = res.dias_detalle.reduce((s, d) => s + d.horas_activas * d.ee, 0);
  const ceuProm = mwhBase > 0 ? res.ce_q / mwhBase : 0;
  const tregProm = mwhBase > 0 ? res.ctreg_q / mwhBase : 0;
  const factorCpi =
    ins.get("cpi") && ins.get("cpi_base")
      ? ins.get("cpi")!.promedio / ins.get("cpi_base")!.promedio
      : null;

  return (
    <>
      {/* Advertencias/narrativa del cálculo: en las facturas preliminar/ajuste
          (prepago gas) aquí viene TODO el ajuste — CE preliminar vs efectivo,
          diferencia y tipo de documento (nota de crédito/complementaria). Antes
          este componente no las pintaba y esa información quedaba invisible. */}
      {res.advertencias.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm space-y-1">
          {res.advertencias.map((a, i) => (
            <div key={i}>⚠ {a}</div>
          ))}
        </div>
      )}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
          <FileText className="w-4 h-4 text-slate-500" />
          <span className="font-medium text-slate-800">
            {res.folio} — {res.contraparte}
          </span>
          <span className="text-xs text-slate-500">
            {res.desde} a {res.hasta} · {res.dias} días · {res.sum_z}/{res.ht_q} h activadas ·{" "}
            {esUsd ? "TC " : "TC_q "}{num(res.tc_q, 4)} · <b>{m}</b>
          </span>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <FilaConcepto
              etiqueta="Cargo por Energía (CE_q)" valor={res.ce_q} moneda={m}
              abierto={open.has("ce")} onToggle={() => toggle("ce")}
              lineas={[
                ["Fórmula", esUsd
                  ? "CE_q = Σ (Horas × EE × PE) · PE = HR×PUC + CVOM×(CPI/CPI_base) USD/MWh"
                  : "CE_q = Σ por día (Horas act. × EE × CEU_d) · CEU_d = [HR×PUC + CVOM×(CPI/CPI_base)] × TC"],
                ["— Valores usados en el periodo —", ""],
                ...lineaIns("Heat Rate (HR)", "hr", 4),
                ...lineaIns("CVOM (USD/MWh)", "cvom", 4),
                ...lineaIns("HSC (USD/MMBtu)", "hsc", 4),
                ...lineaIns("PUC adder (USD/MMBtu)", "puc_adder", 4),
                ...lineaIns("PUC % (sobre HSC)", "puc_pct", 4),
                ...lineaIns("CPI", "cpi", 3),
                ...lineaIns("CPI base", "cpi_base", 3),
                ...(factorCpi ? [["Factor CPI = CPI / CPI base", num(factorCpi, 6)] as [string, string]] : []),
                ...lineaIns("TC del strike (D−1)", "tc", 4),
                ...lineaIns("EE (MWh/h)", "ee", 3),
                ["Horas activadas × EE = MWh liquidados", `${res.sum_z} h → ${num(mwhBase, 3)} MWh`],
                [esUsd ? "PE promedio ponderado (USD/MWh)" : "CEU_d promedio ponderado (MXN/MWh)",
                  num(ceuProm, 4)],
                ["— Resultado —", ""],
                [`CE_q = ${num(mwhBase, 3)} MWh × ${num(ceuProm, 4)}`, money(res.ce_q, m)],
              ]}
            />
            <FilaConcepto
              etiqueta="Tarifas Reguladas (CTreg_q)" valor={res.ctreg_q} moneda={m}
              abierto={open.has("ctreg")} onToggle={() => toggle("ctreg")}
              lineas={[
                ["Fórmula", esUsd
                  ? "CTreg_q = (Σ Horas × EE × TReg) / TC"
                  : "CTreg_q = Σ por día (Horas act. × EE × TReg)"],
                ["— Valores usados en el periodo —", ""],
                ...lineaIns("TReg (MXN/MWh, tarifa CNE)", "treg", 4),
                ...lineaIns("EE (MWh/h)", "ee", 3),
                ["Horas activadas × EE = MWh liquidados", `${res.sum_z} h → ${num(mwhBase, 3)} MWh`],
                ...(esUsd
                  ? ([["TC de conversión (último día hábil)", num(res.tc_q, 4)]] as [string, string][])
                  : []),
                ["— Resultado —", ""],
                [esUsd
                  ? `CTreg_q = ${num(mwhBase, 3)} MWh × TReg / ${num(res.tc_q, 4)}`
                  : `CTreg_q = ${num(mwhBase, 3)} MWh × ${num(tregProm, 4)} MXN/MWh`,
                  money(res.ctreg_q, m)],
              ]}
            />
            {!esUsd && (
              <FilaConcepto
                etiqueta="Cargo Fijo Quincenal (CF_q)" valor={res.cf_q}
                detalle={`PF=${res.pf} / PFTbfin=${res.pf_tbfin} USD/MWh`}
                abierto={open.has("cf")} onToggle={() => toggle("cf")}
                lineas={[
                  ["Fórmula", "CF_q = TC_q × Σ [EE×24×PF + Horas×EE×(PFTbfin−PF)]"],
                  ["Base fija = Σ EE × 24 × PF", `${num(baseFijo)} USD`],
                  ["Uplift = Σ Horas × EE × (PFTbfin − PF)", `${num(upliftFijo)} USD`],
                  ["Total fijo (USD)", num(baseFijo + upliftFijo)],
                  [`CF_q = ${num(baseFijo + upliftFijo)} USD × ${num(res.tc_q, 6)}`, mxn(res.cf_q)],
                ]}
                extra={
                  <TcqDetalle
                    dias={res.dias_detalle} tcq={res.tc_q}
                    abierto={open.has("tcq")} onToggle={() => toggle("tcq")}
                  />
                }
              />
            )}
            {(res.manuales ?? []).map((l, i) => (
              <FilaConcepto
                key={`man-${i}`}
                etiqueta={`${l.monto >= 0 ? "Cargo" : "Crédito"} manual: ${l.concepto}`}
                valor={l.monto} moneda={m}
                abierto={open.has(`man-${i}`)} onToggle={() => toggle(`man-${i}`)}
                lineas={[
                  ["Capturado manualmente al guardar la factura", ""],
                  [l.monto >= 0 ? "Cargo (suma al subtotal, antes de IVA)"
                    : "Crédito (resta del subtotal, antes de IVA)", money(l.monto, m)],
                ]}
              />
            ))}
            <FilaConcepto
              etiqueta="Subtotal (sin IVA)" valor={res.subtotal} bold moneda={m}
              abierto={open.has("sub")} onToggle={() => toggle("sub")}
              lineas={[
                ["Cargo por Energía (CE_q)", money(res.ce_q, m)],
                ["Tarifas Reguladas (CTreg_q)", money(res.ctreg_q, m)],
                ...(esUsd ? [] : [["Cargo Fijo Quincenal (CF_q)", money(res.cf_q, m)] as [string, string]]),
                ...((res.manuales ?? []).length > 0
                  ? [["Cargos/créditos manuales",
                      money((res.manuales ?? []).reduce((s, l) => s + l.monto, 0), m)] as [string, string]]
                  : []),
                [`Subtotal = CE_q + CTreg_q${esUsd ? "" : " + CF_q"}${
                  (res.manuales ?? []).length > 0 ? " + manuales" : ""}`,
                 money(res.subtotal, m)],
              ]}
            />
            <FilaConcepto
              etiqueta={`IVA (${ivaTxt})`} valor={res.iva} moneda={m}
              abierto={open.has("iva")} onToggle={() => toggle("iva")}
              lineas={[
                [`IVA = Subtotal × ${ivaTxt}`, `${money(res.subtotal, m)} × ${res.iva_pct}`],
                ["IVA", money(res.iva, m)],
              ]}
            />
            <FilaConcepto
              etiqueta="TOTAL a cobrar (con IVA)" valor={res.total} total moneda={m}
              abierto={open.has("total")} onToggle={() => toggle("total")}
              lineas={[
                ["Subtotal (sin IVA)", money(res.subtotal, m)],
                [`IVA (${ivaTxt})`, money(res.iva, m)],
                ["TOTAL = Subtotal + IVA", money(res.total, m)],
              ]}
            />
          </tbody>
        </table>
        <div className="px-4 py-2 text-xs text-slate-400 border-t">
          MWh totales registrados en CENACE: {num(res.mwh_total, 2)} MWh · montos en <b>{m}</b> · toca un concepto para ver su desglose.
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b bg-slate-50 text-sm font-medium text-slate-700">
          Detalle diario (memoria de cálculo)
          <span className="font-normal text-slate-400"> — toca un día para ver el cálculo</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="px-3 py-2 w-8" />
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2 text-right">Horas act.</th>
                <th className="px-3 py-2 text-right">Strike</th>
                <th className="px-3 py-2 text-right">CEU_d</th>
                <th className="px-3 py-2 text-right">TReg</th>
                <th className="px-3 py-2 text-right">EE</th>
                <th className="px-3 py-2 text-right">CE día</th>
                <th className="px-3 py-2 text-right">CTreg día</th>
                <th className="px-3 py-2 text-right">MWh</th>
              </tr>
            </thead>
            <tbody>
              {res.dias_detalle.map((d) => (
                <FilaDia
                  key={d.fecha} d={d} idContrato={res.id_contrato} moneda={m} tcPeriodo={res.tc_q}
                  abierto={open.has("dia:" + d.fecha)} onToggle={() => toggle("dia:" + d.fecha)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// Renglón de concepto clicable; al abrir muestra su desglose debajo.
function FilaConcepto({
  etiqueta, valor, detalle, abierto, onToggle, lineas, bold, total, moneda = "MXN", extra,
}: {
  etiqueta: string;
  valor: number;
  detalle?: string;
  abierto: boolean;
  onToggle: () => void;
  lineas: [string, string][];
  bold?: boolean;
  total?: boolean;
  moneda?: string;
  extra?: ReactNode;
}) {
  const rowCls = total
    ? "border-t-2 border-slate-300 font-semibold text-base bg-emerald-50"
    : bold
    ? "border-t font-medium bg-slate-50"
    : "border-t";
  const pad = total ? "py-2.5" : "py-2";
  return (
    <>
      <tr className={`${rowCls} cursor-pointer hover:bg-slate-100/70`} onClick={onToggle}>
        <td className={`px-4 ${pad}`}>
          <span className="inline-flex items-center gap-1.5">
            <ChevronRight
              className={`w-3.5 h-3.5 text-slate-400 transition-transform ${abierto ? "rotate-90" : ""}`}
            />
            {etiqueta}
          </span>
          {detalle && <div className="text-xs text-slate-400 pl-5">{detalle}</div>}
        </td>
        <td className={`px-4 ${pad} text-right tabular-nums ${total ? "text-emerald-800" : ""}`}>
          {money(valor, moneda)}
        </td>
      </tr>
      {abierto && (
        <tr className="bg-slate-50/60">
          <td colSpan={2} className="px-4 pb-3 pt-1">
            <Desglose lineas={lineas} />
            {extra}
          </td>
        </tr>
      )}
    </>
  );
}

// Desglose clicable del promedio TC_q: lista los TC (lag-0, mismo día) que lo componen.
function TcqDetalle({
  dias, tcq, abierto, onToggle,
}: {
  dias: FacturacionDia[];
  tcq: number;
  abierto: boolean;
  onToggle: () => void;
}) {
  const conTc = dias.filter((d) => d.tc_cf != null);
  return (
    <div className="mt-2 max-w-2xl">
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900"
      >
        <ChevronRight
          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${abierto ? "rotate-90" : ""}`}
        />
        TC_q (promedio del periodo) = <b className="tabular-nums">{num(tcq, 6)}</b>
        <span className="text-slate-400"> — ver los {conTc.length} tipos de cambio del promedio</span>
      </button>
      {abierto &&
        (conTc.length === 0 ? (
          <div className="mt-1 text-xs text-slate-400">
            No disponible para esta factura (se guardó antes de esta versión). Vuelve a guardarla
            para incluir el desglose.
          </div>
        ) : (
          <div className="mt-1 rounded border bg-white px-3 py-2 text-xs">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-0.5">
              {conTc.map((d) => (
                <div key={d.fecha} className="flex justify-between gap-4">
                  <span className="text-slate-500">{d.fecha}</span>
                  <span className="tabular-nums">{num(d.tc_cf as number, 4)}</span>
                </div>
              ))}
            </div>
            <div className="border-t mt-1 pt-1 flex justify-between font-medium text-slate-800">
              <span>Promedio = Σ TC ÷ {conTc.length}</span>
              <span className="tabular-nums">{num(tcq, 6)}</span>
            </div>
          </div>
        ))}
    </div>
  );
}

function Desglose({ lineas }: { lineas: [string, string][] }) {
  return (
    <div className="rounded border bg-white px-3 py-2 text-xs space-y-1 max-w-2xl">
      {lineas.map(([k, v], i) =>
        // Una línea sin valor es un separador de sección (p. ej. "— Valores usados —").
        v === "" ? (
          <div key={i} className="pt-1 text-[11px] uppercase tracking-wide text-slate-400">
            {k}
          </div>
        ) : (
          <div
            key={i}
            className={`flex justify-between gap-6 ${
              i === lineas.length - 1 ? "border-t pt-1 font-medium text-slate-800" : "text-slate-500"
            }`}
          >
            <span>{k}</span>
            <span className="tabular-nums whitespace-nowrap">{v}</span>
          </div>
        ),
      )}
    </div>
  );
}

// Renglón de día clicable; al abrir muestra cómo se calculó ese día + el detalle por hora.
function FilaDia({
  d, idContrato, abierto, onToggle, moneda = "MXN", tcPeriodo = 0,
}: {
  d: FacturacionDia;
  idContrato: number;
  abierto: boolean;
  onToggle: () => void;
  moneda?: string;
  tcPeriodo?: number;
}) {
  const esUsd = moneda === "USD";
  // El detalle por hora (PML/activación/MWh) lo da el motor TBFin; se carga al abrir.
  const horasQ = useQuery({
    queryKey: ["tbfin-horas", idContrato, d.fecha],
    queryFn: () => calcularTbfin({ contrato: String(idContrato), fecha: d.fecha }),
    enabled: abierto,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <>
      <tr className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={onToggle}>
        <td className="px-3 py-1.5 text-slate-400">
          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${abierto ? "rotate-90" : ""}`} />
        </td>
        <td className="px-3 py-1.5">{d.fecha}</td>
        <td className="px-3 py-1.5 text-right">{d.horas_activas}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.strike)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.ceu_d)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.treg)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.ee, 0)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{money(d.ce_dia, moneda)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{money(d.ctreg_dia, moneda)}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{num(d.mwh)}</td>
      </tr>
      {abierto && (
        <tr className="bg-slate-50/60">
          <td colSpan={10} className="px-4 py-3">
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-0.5 text-xs max-w-3xl">
              <LineaDia k="Horas activadas (PML ≥ strike)" v={`${d.horas_activas} de 24`} />
              <LineaDia k="EE (volumen)" v={`${num(d.ee, 0)} MWh`} />
              {esUsd && d.pe_usd != null ? (
                <>
                  <LineaDia k="Precio energía PE (USD/MWh)" v={`${num(d.pe_usd, 5)}`} />
                  <LineaDia k="Strike / CEU_d (MXN, ref.)" v={`${num(d.strike, 2)} / ${num(d.ceu_d, 2)}`} />
                  <LineaDia
                    k="CE día = Horas × EE × PE"
                    v={`${d.horas_activas} × ${num(d.ee, 0)} × ${num(d.pe_usd, 5)} = ${money(d.ce_dia, moneda)}`}
                  />
                  <LineaDia k="TReg" v={`${num(d.treg, 5)} MXN/MWh`} />
                  <LineaDia k="TC (último día hábil)" v={num(tcPeriodo, 4)} />
                  <LineaDia
                    k="CTreg día = Horas × EE × TReg / TC"
                    v={`${d.horas_activas} × ${num(d.ee, 0)} × ${num(d.treg, 5)} / ${num(tcPeriodo, 4)} = ${money(d.ctreg_dia, moneda)}`}
                  />
                </>
              ) : (
                <>
                  <LineaDia k="TC del día (D−1)" v={num(d.tc_d, 4)} />
                  <LineaDia k="Strike (umbral)" v={`${num(d.strike, 5)} MXN/MWh`} />
                  <LineaDia k="TReg" v={`${num(d.treg, 5)} MXN/MWh`} />
                  <LineaDia
                    k="CEU_d = Strike − TReg"
                    v={`${num(d.strike, 5)} − ${num(d.treg, 5)} = ${num(d.ceu_d, 5)}`}
                  />
                  <LineaDia
                    k="CE día = Horas × EE × CEU_d"
                    v={`${d.horas_activas} × ${num(d.ee, 0)} × ${num(d.ceu_d, 5)} = ${money(d.ce_dia, moneda)}`}
                  />
                  <LineaDia
                    k="CTreg día = Horas × EE × TReg"
                    v={`${d.horas_activas} × ${num(d.ee, 0)} × ${num(d.treg, 5)} = ${money(d.ctreg_dia, moneda)}`}
                  />
                </>
              )}
              <LineaDia k="MWh programados" v={`${num(d.mwh)} MWh`} />
            </div>

            {/* Detalle por hora (PML / activación / MWh del MDA) */}
            <div className="mt-3">
              <div className="text-xs font-medium text-slate-600 mb-1">
                Detalle por hora — activa si PML ≥ strike
              </div>
              {horasQ.isLoading && (
                <div className="text-xs text-slate-400">Cargando detalle por hora…</div>
              )}
              {horasQ.isError && (
                <div className="text-xs text-red-600">
                  No se pudo cargar el detalle por hora: {(horasQ.error as Error).message}
                </div>
              )}
              {horasQ.data && (
                <div className="overflow-x-auto">
                  <table className="text-xs border rounded bg-white min-w-[28rem]">
                    <thead>
                      <tr className="text-left text-slate-500 border-b">
                        <th className="px-2.5 py-1">Hora</th>
                        <th className="px-2.5 py-1 text-right">PML (MXN/MWh)</th>
                        <th className="px-2.5 py-1 text-right">Strike</th>
                        <th className="px-2.5 py-1 text-center">¿Activa?</th>
                        <th className="px-2.5 py-1 text-right">MWh</th>
                        <th className="px-2.5 py-1 text-right">Total (MXN)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {horasQ.data.horas.map((h) => (
                        <tr key={h.hora} className={`border-b last:border-0 ${h.activada ? "bg-emerald-50" : ""}`}>
                          <td className="px-2.5 py-1 tabular-nums">{h.hora}</td>
                          <td className="px-2.5 py-1 text-right tabular-nums">
                            {h.pml == null ? "—" : num(h.pml)}
                          </td>
                          <td className="px-2.5 py-1 text-right tabular-nums text-slate-500">
                            {num(horasQ.data!.strike)}
                          </td>
                          <td className="px-2.5 py-1 text-center">
                            {h.activada ? (
                              <span className="text-emerald-700 font-medium">Sí</span>
                            ) : (
                              <span className="text-slate-400">No</span>
                            )}
                          </td>
                          <td className="px-2.5 py-1 text-right tabular-nums font-medium">{num(h.mwh, 3)}</td>
                          <td className="px-2.5 py-1 text-right tabular-nums">
                            {h.total ? mxn(h.total) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function LineaDia({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-0.5">
      <span className="text-slate-500">{k}</span>
      <span className="tabular-nums font-medium text-slate-700 whitespace-nowrap">{v}</span>
    </div>
  );
}
